import { useState, useEffect } from 'react';
import { Users, Check, AlertTriangle, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';

// Type definitions
interface Hospital {
  id: number;
  name: string;
  location: string;
  address:string
}

interface CareTeamMember {
  user_id: number;
  user_name: string;
  role: string;
}

interface CareTeam {
  id: number;
  name: string;
  hospital_id: number;
  hospital?: {
    id: number;
    name: string;
    location: string;
  };
  members?: CareTeamMember[];
  is_active: boolean;
}

interface PatientAssignmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: {
    id: string | number;
    name: string;
    age: number;
    status: string;
    hospital_id?: number;
  };
  currentAssignment?: {
    careTeamId?: string | number;
    care_team_id?: number;
  };
  hospitals?: Hospital[];
  careTeams?: CareTeam[];
  onAssign: (assignment: {
    patient_id: number;
    care_team_id: number;
  }) => void;
  isReassignment?: boolean;
}

export function PatientAssignmentModal({
  open,
  onOpenChange,
  patient,
  currentAssignment,
  hospitals = [],
  careTeams = [],
  onAssign,
  isReassignment = false,
}: PatientAssignmentModalProps) {
  const [selectedHospital, setSelectedHospital] = useState<string>('');
  const [selectedCareTeam, setSelectedCareTeam] = useState<string>('');

  // Initialize with patient's hospital and current care team assignment
  useEffect(() => {
    if (open) {
      // Set patient's hospital if available
      if (patient.hospital_id) {
        setSelectedHospital(patient.hospital_id.toString());
      }
      
      // Set current care team if reassigning
      if (currentAssignment?.care_team_id) {
        setSelectedCareTeam(currentAssignment.care_team_id.toString());
      } else if (currentAssignment?.careTeamId) {
        setSelectedCareTeam(currentAssignment.careTeamId.toString());
      }
    } else {
      // Reset when modal closes
      setSelectedHospital('');
      setSelectedCareTeam('');
    }
  }, [open, patient.hospital_id, currentAssignment]);

  // Filter care teams by selected hospital
  const filteredCareTeams = selectedHospital
    ? careTeams.filter(ct => {
        const teamHospitalId = ct.hospital_id?.toString() || ct.hospital?.id?.toString();
        return teamHospitalId === selectedHospital && ct.is_active !== false;
      })
    : careTeams.filter(ct => ct.is_active !== false);

  // Get selected care team details
  const careTeam = careTeams.find(ct => ct.id.toString() === selectedCareTeam);

  const handleHospitalChange = (hospitalId: string) => {
    setSelectedHospital(hospitalId);
    // Reset care team when hospital changes
    setSelectedCareTeam('');
  };

  const handleAssign = () => {
    if (!selectedCareTeam) {
      toast.error('Please select a care team', {
        description: 'You must select a care team to assign the patient',
      });
      return;
    }

    // Call the parent's onAssign with simplified data
    onAssign({
      patient_id: typeof patient.id === 'string' ? parseInt(patient.id) : patient.id,
      care_team_id: parseInt(selectedCareTeam),
    });

    const message = isReassignment
      ? 'Patient reassigned successfully!'
      : 'Patient assigned successfully!';
    
    toast.success(message, {
      description: `${patient.name} has been ${isReassignment ? 'reassigned to' : 'assigned to'} ${careTeam?.name}. All care team members can now access this patient.`,
    });

    onOpenChange(false);
  };

  const hasChanges = isReassignment && (
    selectedCareTeam !== currentAssignment?.care_team_id?.toString() &&
    selectedCareTeam !== currentAssignment?.careTeamId?.toString()
  );

  // Get care team member count by role
  const getCareTeamSummary = (team: any) => {
    const members = [];

    // Doctors
    if (team.doctors) {
      for (const doc of team.doctors) {
        members.push({ role: "DOCTOR", ...doc });
      }
    }

    // Physician Assistants
    if (team.physician_assistants) {
      for (const pa of team.physician_assistants) {
        members.push({ role: "ASSISTANT_DOCTOR", ...pa });
      }
    }

    // Therapist
    if (team.therapist) {
      members.push({ role: "THERAPIST", ...team.therapist });
    }

    // Nurse
    if (team.nurse) {
      members.push({ role: "NURSE", ...team.nurse });
    }

    if (members.length === 0) return "No members";

    const roles = members.reduce((acc, m) => {
      acc[m.role] = (acc[m.role] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const parts = [];
    if (roles.DOCTOR) parts.push(`${roles.DOCTOR} Doctor${roles.DOCTOR > 1 ? "s" : ""}`);
    if (roles.ASSISTANT_DOCTOR) parts.push(`${roles.ASSISTANT_DOCTOR} Assistant${roles.ASSISTANT_DOCTOR > 1 ? "s" : ""}`);
    if (roles.THERAPIST) parts.push(`${roles.THERAPIST} Therapist${roles.THERAPIST > 1 ? "s" : ""}`);
    if (roles.NURSE) parts.push(`${roles.NURSE} Nurse${roles.NURSE > 1 ? "s" : ""}`);

    return parts.join(", ");
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-primary text-xl">
            {isReassignment ? 'Reassign Patient' : 'Assign Patient to Care Team'}
          </DialogTitle>
          <DialogDescription>
            {isReassignment
              ? 'Change the care team assignment for this patient'
              : 'Select a hospital and care team. All members of the care team will have access to this patient.'}
          </DialogDescription>
        </DialogHeader>

        {/* Patient Info */}
        <Card className="bg-[#D4F0F8] border-none">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-primary-foreground">
                  {patient.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div className="flex-1">
                <p className="font-medium text-primary">{patient.name}</p>
                <p className="text-sm text-muted-foreground">{patient.age} years</p>
              </div>
              <Badge variant="outline" className="bg-white">
                {patient.status}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Reassignment Warning */}
        {isReassignment && hasChanges && (
          <div className="bg-[#FEEBDA] border border-[#F07D13]/30 p-4 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#F07D13] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-[#494949]">
                <strong>Warning:</strong> Reassigning will change which care team members have access to this patient's data.
              </p>
            </div>
          </div>
        )}

        {/* Assignment Form */}
        <div className="space-y-6">
          {/* Hospital Selection */}
          <div className="space-y-2">
            <Label htmlFor="hospital" className="text-[#2762AC]">
              <Building2 className="w-4 h-4 inline mr-1" />
              Hospital *
            </Label>
            <Select value={selectedHospital} onValueChange={handleHospitalChange}>
              <SelectTrigger id="hospital" className="bg-[#E5E5E3]">
                <SelectValue placeholder="Select a hospital" />
              </SelectTrigger>
              <SelectContent>
                {hospitals.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground">
                    No hospitals available
                  </div>
                ) : (
                  hospitals.map(hospital => (
                    <SelectItem key={hospital.id} value={hospital.id.toString()}>
                      <div className="flex items-center justify-between w-full">
                        <span>{hospital.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {hospital.address}
                        </span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {selectedHospital && (
              <p className="text-xs text-muted-foreground">
                Showing care teams from selected hospital
              </p>
            )}
          </div>

          {/* Care Team Selection */}
          <div className="space-y-2">
            <Label htmlFor="careTeam" className="text-[#2762AC]">
              <Users className="w-4 h-4 inline mr-1" />
              Care Team *
            </Label>
            <Select 
              value={selectedCareTeam} 
              onValueChange={setSelectedCareTeam}
              disabled={!selectedHospital}
            >
              <SelectTrigger id="careTeam" className="bg-[#E5E5E3]">
                <SelectValue placeholder={selectedHospital ? "Select a care team" : "Select hospital first"} />
              </SelectTrigger>
              <SelectContent>
                {filteredCareTeams.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground">
                    {selectedHospital 
                      ? "No active care teams in this hospital" 
                      : "Please select a hospital first"}
                  </div>
                ) : (
                  filteredCareTeams.map(team => (
                    <SelectItem key={team.id} value={team.id.toString()}>
                      <div className="flex flex-col">
                        <span>{team.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {getCareTeamSummary(team)}
                        </span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {careTeam && (
              <div className="text-xs text-muted-foreground space-y-1">
                <p>{careTeam.hospital?.name || 'Hospital'}</p>
                <p>{getCareTeamSummary(careTeam)}</p>
              </div>
            )}
          </div>

          {/* Info Message */}
          <div className="bg-[#D4F0F8] border border-[#14CAF4]/30 p-4 rounded-lg">
            <p className="text-sm text-[#494949]">
              <strong>Note:</strong> All members of the selected care team (doctors, therapists, and nurses) will automatically have access to this patient's medical records and can provide care.
            </p>
          </div>

          {/* Preview Card */}
          {selectedCareTeam && careTeam && (
            <Card className="border-[#14CAF4] bg-[#14CAF4]/5">
              <CardContent className="p-4">
                <h4 className="text-sm text-primary mb-3 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Assignment Preview
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Hospital:</span>
                    <span className="font-medium">
                      {hospitals.find(h => h.id.toString() === selectedHospital)?.name}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Care Team:</span>
                    <span className="font-medium">{careTeam.name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Team Members:</span>
                    <span className="font-medium">{getCareTeamSummary(careTeam)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[#494949] text-[#494949]"
          >
            Cancel
          </Button>

          <Button
            onClick={handleAssign}
            className="bg-[#F07D13] hover:bg-[#F07D13]/90"
            disabled={!selectedCareTeam}
          >
            <Check className="w-4 h-4 mr-2" />
            {isReassignment ? 'Reassign Patient' : 'Assign Patient'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
